import { useState, useEffect } from 'react'
import { useApi } from '../hooks/useApi'
import { Nav } from '../components/Nav'
import { Loading } from '../components/Loading'
import { trackInsightsViewed } from '../lib/analytics'
import { ContextualHint, HINTS } from '../components/ContextualHint'

interface Theme {
  name: string
  score: number
  evidence: string[]
}

interface ThemeSnapshot {
  themes: Theme[]
  metaphors: { phrase: string; count: number }[]
  narrative_summary: string | null
  top_distortions: { name: string; count: number }[]
  time_orientation: { past: number; present: number; future: number }
  created_at: string | null
}

export default function Insights() {
  const { get } = useApi()

  const [data, setData] = useState<ThemeSnapshot | null>(null)
  const [loading, setLoading] = useState(true)

  // Track page view
  useEffect(() => {
    trackInsightsViewed()
  }, [])

  useEffect(() => {
    const loadThemes = async () => {
      try {
        const result = await get<ThemeSnapshot>('/api/themes/latest')
        setData(result)
      } catch (error) {
        console.error('Failed to load themes:', error)
      } finally {
        setLoading(false)
      }
    }

    loadThemes()
  }, [get])

  const hasData = data && data.themes && data.themes.length > 0

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Insights</h1>
      </div>

      <div className="page-content">
        <ContextualHint id={HINTS.INSIGHTS_FIRST.id} icon={HINTS.INSIGHTS_FIRST.icon}>
          {HINTS.INSIGHTS_FIRST.content}
        </ContextualHint>

        {loading ? (
          <Loading message="Loading insights..." />
        ) : !hasData ? (
          <div className="empty-state">
            <div className="empty-state-icon">📊</div>
            <h3 className="empty-state-title">Patterns Coming Soon</h3>
            <p className="empty-state-text">
              Complete a few more sessions and we'll analyze your patterns, themes, and metaphors.
            </p>
          </div>
        ) : (
          <>
            {/* Narrative summary */}
            {data.narrative_summary && (
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ marginBottom: '0.5rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                  Your Story
                </h3>
                <p style={{ lineHeight: 1.6 }}>{data.narrative_summary}</p>
              </div>
            )}

            {/* Themes */}
            {data.themes.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ marginBottom: '0.75rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                  Recurring Themes
                </h3>
                {data.themes.map((theme, i) => (
                  <div key={i} className="card" style={{ marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ fontWeight: '600' }}>{theme.name}</span>
                      <span style={{ color: 'var(--color-primary)' }}>{Math.round(theme.score * 100)}%</span>
                    </div>
                    <div className="intensity-bar">
                      <div
                        className="intensity-fill"
                        style={{
                          width: `${theme.score * 100}%`,
                          backgroundColor: 'var(--color-primary)'
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Top distortions */}
            {data.top_distortions && data.top_distortions.length > 0 && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ marginBottom: '0.75rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                  Common Patterns
                </h3>
                <div className="distortion-tags">
                  {data.top_distortions.map((d, i) => (
                    <span key={i} className="distortion-tag">
                      {d.name} ({d.count})
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Time orientation */}
            {data.time_orientation && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ marginBottom: '0.75rem', fontSize: '0.875rem', color: 'var(--color-text-muted)' }}>
                  Time Focus
                </h3>
                <div className="stats-summary">
                  <div className="stat-item">
                    <div className="stat-value">{data.time_orientation.past || 0}%</div>
                    <div className="stat-label">Past</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">{data.time_orientation.present || 0}%</div>
                    <div className="stat-label">Present</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-value">{data.time_orientation.future || 0}%</div>
                    <div className="stat-label">Future</div>
                  </div>
                </div>
              </div>
            )}

            {data.created_at && (
              <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', textAlign: 'center' }}>
                Last updated: {new Date(data.created_at).toLocaleDateString()}
              </p>
            )}
          </>
        )}
      </div>

      <Nav />
    </div>
  )
}
